import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const fmtMoney = (cents, ccy) => {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (ccy || 'usd').toUpperCase(),
    }).format((cents || 0) / 100);
  } catch (_) {
    return `${(cents / 100).toFixed(2)} ${(ccy || '').toUpperCase()}`;
  }
};

/**
 * Full-or-partial refund dialog for the SubscriptionsTab. The default
 * is a full refund of the most recent payment. Switching to "partial"
 * shows an amount input bounded by the invoice total we already have on
 * the subscription row.
 *
 * Caller owns the open state and the actual API call — we just collect
 * the inputs and hand back either `null` (full) or a number of cents.
 */
export default function RefundDialog({ open, sub, busy, onConfirm, onCancel }) {
  const [mode, setMode] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');

  const item = sub?.items?.[0];
  const currency = item?.currency || 'usd';
  const invoiceCents = Number.isFinite(item?.unit_amount) ? item.unit_amount : null;

  useEffect(() => {
    if (open) {
      setMode('full');
      setPartialAmount('');
    }
  }, [open]);

  const partialCents = (() => {
    const n = parseFloat(partialAmount);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n * 100);
  })();

  const partialInvalid = mode === 'partial' && (
    partialCents == null || (invoiceCents != null && partialCents > invoiceCents)
  );

  const submit = () => {
    if (mode === 'partial' && partialInvalid) return;
    onConfirm(mode === 'partial' ? partialCents : null);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle>Refund latest payment</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' mb='12px'>
          Refunds the most recent successful payment on{' '}
          <strong>{sub?.customer?.email || sub?.customer?.id}</strong>'s card.
          The subscription stays active — cancel separately if you want to
          stop future billing.
        </Typography>

        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value='full'
            control={<Radio size='small' />}
            label={`Full refund${invoiceCents != null ? ` (${fmtMoney(invoiceCents, currency)})` : ''}`}
          />
          <FormControlLabel
            value='partial'
            control={<Radio size='small' />}
            label='Partial refund'
          />
        </RadioGroup>

        {mode === 'partial' && (
          <Box mt='8px' ml='30px'>
            <Stack direction='row' alignItems='center' spacing='8px'>
              <TextField
                size='small'
                type='number'
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                placeholder='0.00'
                inputProps={{ step: '0.01', min: '0' }}
                sx={{ width: 140 }}
                error={!!partialInvalid}
              />
              <Typography variant='body2' color='text.secondary'>
                {currency.toUpperCase()}
              </Typography>
            </Stack>
            {invoiceCents != null && (
              <Typography variant='caption' color='text.secondary' mt='4px' display='block'>
                Max: {fmtMoney(invoiceCents, currency)}
              </Typography>
            )}
            {partialInvalid && partialCents != null && invoiceCents != null && partialCents > invoiceCents && (
              <Typography variant='caption' color='error.main' display='block'>
                Exceeds the invoice total.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          variant='contained'
          color='warning'
          disabled={busy || (mode === 'partial' && partialInvalid)}
          onClick={submit}
        >
          {busy ? 'Refunding…' : 'Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
